import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  Description,
  CheckCircle,
  Error,
  FolderOpen,
  CloudDone,
  AddCircleOutline,
  History as HistoryIcon,
  Upload,
  ArrowBack,
  AttachMoney,
  CalendarToday,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import ProposalViewer from '../components/ProposalViewer';
import StatsCard from '../components/StatsCard';

interface UserStats {
  totalProposals: number;
  inProgressProposals: number;
  completedProposals: number;
  failedProposals: number;
  contextDocumentsCount: number;
  googleDriveConnected: boolean;
  lastSyncedAt?: string;
  totalCost: number;
  weeklyCost: number;
  recentProposals: Array<{
    id: string;
    rfpName: string;
    status: 'in_progress' | 'completed' | 'failed';
    createdAt: string;
  }>;
}

interface QuestionSummary {
  totalQuestions: number;
  completedQuestions: number;
  failedQuestions: number;
  skippedQuestions: number;
  insufficientInfoQuestions: number;
  failedQuestionDetails?: Array<{
    questionNumber: number;
    questionText: string;
    status: string;
    reason?: string;
  }>;
}

interface ProposalData {
  id: string;
  rfpId: string;
  rfpName: string;
  content: string;
  sections: Array<{
    title: string;
    content: string;
  }>;
  status: 'in_progress' | 'completed' | 'failed';
  questionSummary?: QuestionSummary;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ProposalData | null>(null);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);

  const proposalId = searchParams.get('proposalId');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (proposalId) {
      fetchProposal(proposalId);
    } else {
      setProposal(null);
    }
  }, [proposalId]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_ENDPOINTS.users.stats, {
        withCredentials: true,
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch user stats:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProposal = async (id: string) => {
    try {
      setProposalLoading(true);
      setProposalError(null);
      const response = await axios.get(API_ENDPOINTS.proposals.get(id), {
        withCredentials: true,
      });
      setProposal(response.data.proposal);
    } catch (err) {
      console.error('Failed to fetch proposal:', err);
      setProposalError('Failed to load proposal');
    } finally {
      setProposalLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    setSearchParams({});
    setProposal(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatCost = (cost?: number) => {
    if (cost === undefined || cost === null) return '$0.00';
    return `$${cost.toFixed(2)}`;
  };

  // If viewing a specific proposal
  if (proposalId) {
    if (proposalLoading) {
      return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    if (proposalError) {
      return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Stack spacing={2}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToDashboard}
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
            >
              Back to Dashboard
            </Button>
            <Alert severity="error">{proposalError}</Alert>
          </Stack>
        </Container>
      );
    }

    if (proposal) {
      return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Stack spacing={3}>
            <Box>
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToDashboard}
                variant="outlined"
                sx={{ mb: 2 }}
              >
                Back to Dashboard
              </Button>
              <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
                {proposal.rfpName}
              </Typography>
              <Chip
                label={proposal.status === 'completed' ? 'Completed' : proposal.status}
                color={proposal.status === 'completed' ? 'success' : 'default'}
                size="small"
              />
            </Box>
            <ProposalViewer
              content={proposal.content}
              rfpId={proposal.rfpId}
              questionSummary={proposal.questionSummary}
            />
          </Stack>
        </Container>
      );
    }
  }

  // Default dashboard view
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const successRate = stats?.totalProposals
    ? Math.round((stats.completedProposals / stats.totalProposals) * 100)
    : 0;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
      {/* Welcome Section */}
      <Box mb={5}>
        <Typography
          variant="h4"
          component="h1"
          fontWeight={700}
          sx={{
            fontSize: { xs: '28px', md: '32px' },
            mb: 1,
            letterSpacing: '-0.01em'
          }}
        >
          Welcome back, {user?.email?.split('@')[0]}!
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#64748B',
            fontSize: '16px',
            lineHeight: 1.6
          }}
        >
          Here's an overview of your proposal generation activity
        </Typography>
      </Box>

      {/* Quick Actions */}
      <Stack
        direction="row"
        spacing={2}
        mb={5}
        flexWrap="wrap"
        sx={{ gap: 1.5 }}
      >
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={() => navigate('/app/generate')}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            px: 3,
            py: 1.25,
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #5a3a7a 100%)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            },
          }}
        >
          New Proposal
        </Button>
        <Button
          variant="outlined"
          startIcon={<Upload />}
          onClick={() => navigate('/app/context')}
          sx={{
            px: 3,
            py: 1.25,
            fontSize: '15px',
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#E2E8F0',
            color: '#475569',
            '&:hover': {
              borderColor: '#CBD5E1',
              backgroundColor: '#F8FAFC',
            },
          }}
        >
          Upload Context
        </Button>
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={() => navigate('/app/history')}
          sx={{
            px: 3,
            py: 1.25,
            fontSize: '15px',
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#E2E8F0',
            color: '#475569',
            '&:hover': {
              borderColor: '#CBD5E1',
              backgroundColor: '#F8FAFC',
            },
          }}
        >
          View History
        </Button>
      </Stack>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={5}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              boxShadow: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: '#CBD5E1',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '10px',
                    backgroundColor: '#F1F5F9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#667eea',
                  }}
                >
                  <Description sx={{ fontSize: 24 }} />
                </Box>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: '36px',
                  fontWeight: 700,
                  mb: 0.5,
                  color: '#1E293B',
                  lineHeight: 1.2,
                }}
              >
                {stats?.totalProposals || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#64748B',
                  letterSpacing: '0.01em',
                }}
              >
                Total Proposals
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              boxShadow: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: '#CBD5E1',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '10px',
                    backgroundColor: '#F1F5F9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#3B82F6',
                  }}
                >
                  <TrendingUp sx={{ fontSize: 24 }} />
                </Box>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: '36px',
                  fontWeight: 700,
                  mb: 0.5,
                  color: '#1E293B',
                  lineHeight: 1.2,
                }}
              >
                {stats?.inProgressProposals || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#64748B',
                  letterSpacing: '0.01em',
                }}
              >
                In Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              boxShadow: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: '#CBD5E1',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '10px',
                    backgroundColor: '#F1F5F9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#10B981',
                  }}
                >
                  <CheckCircle sx={{ fontSize: 24 }} />
                </Box>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: '36px',
                  fontWeight: 700,
                  mb: 0.5,
                  color: '#1E293B',
                  lineHeight: 1.2,
                }}
              >
                {stats?.completedProposals || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#64748B',
                  letterSpacing: '0.01em',
                }}
              >
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              boxShadow: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: '#CBD5E1',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '10px',
                    backgroundColor: '#F1F5F9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8B5CF6',
                  }}
                >
                  <FolderOpen sx={{ fontSize: 24 }} />
                </Box>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: '36px',
                  fontWeight: 700,
                  mb: 0.5,
                  color: '#1E293B',
                  lineHeight: 1.2,
                }}
              >
                {stats?.contextDocumentsCount || 0}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#64748B',
                  letterSpacing: '0.01em',
                }}
              >
                Context Docs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cost Statistics */}
      <Grid container spacing={3} mb={5}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <StatsCard
            title="Total Cost"
            value={formatCost(stats?.totalCost)}
            icon={<AttachMoney sx={{ fontSize: 20 }} />}
            color="#10B981"
            subtitle="All time"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <StatsCard
            title="Weekly Cost"
            value={formatCost(stats?.weeklyCost)}
            icon={<CalendarToday sx={{ fontSize: 20 }} />}
            color="#3B82F6"
            subtitle="Last 7 days"
          />
        </Grid>
      </Grid>

      {/* Additional Info Cards */}
      <Grid container spacing={3}>
        {/* Success Rate & Status */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              boxShadow: 'none',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  mb: 3,
                  color: '#1E293B',
                }}
              >
                Performance
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#64748B',
                      mb: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Success Rate
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#1E293B',
                      }}
                    >
                      {successRate}%
                    </Typography>
                    {successRate >= 80 && <CheckCircle sx={{ color: '#10B981', fontSize: 24 }} />}
                    {successRate < 80 && successRate > 0 && <Error sx={{ color: '#F59E0B', fontSize: 24 }} />}
                  </Box>
                </Box>

                {stats && stats.failedProposals > 0 && (
                  <Box
                    sx={{
                      pt: 2,
                      borderTop: '1px solid #F1F5F9',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        color: '#64748B',
                      }}
                    >
                      Failed: <strong>{stats.failedProposals}</strong>
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    pt: 2,
                    borderTop: '1px solid #F1F5F9',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#64748B',
                      mb: 1.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Google Drive
                  </Typography>
                  <Chip
                    icon={stats?.googleDriveConnected ? <CloudDone /> : undefined}
                    label={stats?.googleDriveConnected ? 'Connected' : 'Not Connected'}
                    color={stats?.googleDriveConnected ? 'success' : 'default'}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: '13px',
                    }}
                  />
                  {stats?.lastSyncedAt && (
                    <Typography
                      variant="caption"
                      display="block"
                      mt={1.5}
                      sx={{
                        fontSize: '12px',
                        color: '#94A3B8',
                      }}
                    >
                      Last synced: {formatDate(stats.lastSyncedAt)}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              boxShadow: 'none',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  mb: 3,
                  color: '#1E293B',
                }}
              >
                Recent Proposals
              </Typography>

              {!stats?.recentProposals || stats.recentProposals.length === 0 ? (
                <Box
                  sx={{
                    py: 6,
                    textAlign: 'center',
                    backgroundColor: '#F8FAFC',
                    borderRadius: '8px',
                    border: '1px dashed #CBD5E1',
                  }}
                >
                  <Description sx={{ fontSize: 48, color: '#CBD5E1', mb: 2 }} />
                  <Typography sx={{ color: '#64748B', fontSize: '15px' }}>
                    No proposals yet. Click "New Proposal" to get started!
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={0}>
                  {stats.recentProposals.map((proposal, index) => (
                    <Box key={proposal.id}>
                      <Box
                        sx={{
                          py: 2,
                          px: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          cursor: 'pointer',
                          borderRadius: '8px',
                          transition: 'background-color 0.15s',
                          '&:hover': {
                            backgroundColor: '#F8FAFC',
                          },
                        }}
                        onClick={() => {
                          if (proposal.status === 'completed') {
                            navigate(`/app/dashboard?proposalId=${proposal.id}`);
                          } else {
                            navigate('/app/generate');
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            backgroundColor: '#F1F5F9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Description sx={{ fontSize: 20, color: '#667eea' }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '15px',
                              fontWeight: 500,
                              color: '#1E293B',
                              mb: 0.25,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {proposal.rfpName}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: '#94A3B8',
                            }}
                          >
                            {formatDate(proposal.createdAt)}
                          </Typography>
                        </Box>
                        <Chip
                          label={getStatusLabel(proposal.status)}
                          color={getStatusColor(proposal.status) as 'success' | 'info' | 'error' | 'default'}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: '12px',
                            height: '24px',
                          }}
                        />
                      </Box>
                      {index < stats.recentProposals.length - 1 && (
                        <Divider sx={{ borderColor: '#F1F5F9' }} />
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
